import { Modal, Select, InputNumber, Row, Col, Typography, Card, Statistic } from "antd";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;
const { Option } = Select;

const UniversityCalculator = ({ visible, onCancel }) => {
    const { t } = useTranslation();
    const { list: universities } = useSelector((state) => state.university) || {};

    const [selectedUniversity, setSelectedUniversity] = useState(null);
    const [selectedMajor, setSelectedMajor] = useState(null);
    const [grant, setGrant] = useState(0);
    const [priceAfterGrant, setPriceAfterGrant] = useState(0);
    const [majorScholarships, setMajorScholarships] = useState([]);

    // Filtered majors based on selected university
    const [universityMajors, setUniversityMajors] = useState([]);

    useEffect(() => {
        if (selectedUniversity) {
            const uni = universities?.find(u => u.id === selectedUniversity);
            setUniversityMajors(uni?.majors || []);
            setSelectedMajor(null);
        } else {
            setUniversityMajors([]);
        }
    }, [selectedUniversity, universities]);

    useEffect(() => {
        if (selectedMajor) {
            const major = universityMajors.find(m => m.id === selectedMajor);
            const originalPrice = parseFloat(major?.price) || 0;

            let scholarships = [];
            if (major?.scholarships) {
                try {
                    scholarships = typeof major.scholarships === 'string' ? JSON.parse(major.scholarships) : major.scholarships;
                } catch (e) {
                    console.error("Failed to parse major scholarships", e);
                }
            }
            setMajorScholarships(scholarships);

            // If scholarships exist, default to the first one or 0 if none
            if (scholarships.length > 0) {
                // Keep current grant if it exists in the list, otherwise use first
                const exists = scholarships.find(s => s.percentage === grant);
                if (!exists) {
                    setGrant(scholarships[0].percentage);
                    setPriceAfterGrant(parseFloat(scholarships[0].price) || 0);
                } else {
                    setPriceAfterGrant(parseFloat(exists.price) || 0);
                }
            } else {
                const finalPrice = originalPrice * (1 - grant / 100);
                setPriceAfterGrant(finalPrice);
            }
        } else {
            setPriceAfterGrant(0);
            setMajorScholarships([]);
        }
    }, [selectedMajor, grant, universityMajors]);

    const handleUniversityChange = (value) => {
        setSelectedUniversity(value);
    };

    const handleMajorChange = (value) => {
        setSelectedMajor(value);
    };

    const handleGrantChange = (value) => {
        setGrant(value || 0);
    };

    const currentMajor = universityMajors.find(m => m.id === selectedMajor);

    return (
        <Modal
            title={t("university.calculator.title")}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
            className="rounded-xl overflow-hidden"
            bodyStyle={{ padding: '24px' }}
        >
            <div className="space-y-6">
                <Row gutter={[16, 24]}>
                    <Col span={24}>
                        <div className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t("university.calculator.university")}
                        </div>
                        <Select
                            placeholder={t("university.calculator.selectUniversity")}
                            className="w-full"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                            onChange={handleUniversityChange}
                            value={selectedUniversity}
                        >
                            {universities?.map(uni => (
                                <Option key={uni.id} value={uni.id}>{uni.universityName}</Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={24}>
                        <div className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t("university.calculator.major")}
                        </div>
                        <Select
                            placeholder={t("university.calculator.selectMajor")}
                            className="w-full"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                            onChange={handleMajorChange}
                            value={selectedMajor}
                            disabled={!selectedUniversity}
                        >
                            {universityMajors.map(major => (
                                <Option key={major.id} value={major.id}>{major.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    {majorScholarships.filter(s => s.percentage > 0).length > 0 && (
                        <Col span={24}>
                            <div className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("university.scholarships") || "Available Scholarships"}
                            </div>
                            <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1 custom-scrollbar">
                                {majorScholarships.filter(s => s.percentage > 0).sort((a, b) => b.percentage - a.percentage).map((s, idx) => (
                                    <div

                                        key={idx}
                                        onClick={() => handleGrantChange(s.percentage)}
                                        className={`p-3 border rounded-xl cursor-pointer transition-all flex justify-between items-center group ${grant === s.percentage ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500/20' : 'border-gray-100 bg-white hover:border-blue-200 hover:bg-gray-50/50'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${grant === s.percentage ? 'border-blue-500' : 'border-gray-300'}`}>
                                                {grant === s.percentage && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                                            </div>
                                            <span className={`font-semibold transition-colors ${grant === s.percentage ? 'text-blue-700' : 'text-gray-700'}`}>
                                                {s.percentage}% {t("university.scholarship") || "Scholarship"}
                                            </span>
                                        </div>
                                        <div className="text-right">
                                            <div className={`font-bold text-lg transition-colors ${grant === s.percentage ? 'text-blue-600' : 'text-gray-900'}`}>
                                                {s.price} <span className="text-xs font-medium text-gray-500">{currentMajor?.currency || "USD"}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    )}
                </Row>

                <Card className="bg-blue-50/50 border-blue-100 rounded-xl mt-4">
                    <Row gutter={16} align="middle">
                        <Col span={12}>
                            <Statistic
                                title={t("university.calculator.priceBeforeGrant")}
                                value={currentMajor?.price || 0}
                                precision={2}
                                valueStyle={{ color: '#64748b', fontSize: '1.2rem' }}
                                suffix={currentMajor?.currency || "$"}
                            />
                        </Col>
                        <Col span={12}>
                            <Statistic
                                title={t("university.calculator.priceAfterGrant")}
                                value={priceAfterGrant}
                                precision={2}
                                valueStyle={{ color: '#2563eb', fontWeight: 'bold', fontSize: '1.5rem' }}
                                suffix={currentMajor?.currency || "$"}
                            />
                        </Col>
                    </Row>
                </Card>

                {currentMajor && (
                    <div className="text-center">
                        <Text type="secondary" className="text-xs">
                            * {t("university.calculator.disclaimer") || "This is an estimated calculation based on current prices."}
                        </Text>
                    </div>
                )}
            </div>
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }
            `}</style>
        </Modal>
    );
};

export default UniversityCalculator;
